import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiUrl } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { hasFrontendPermission } from '../permissions';

const STATUS_OPTIONS = ['pending', 'paid', 'overdue', 'cancelled'];

const STATUS_LABELS = {
  pending: 'Pending',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

const makeEmptyDraft = () => ({
  jobId: '',
  customerId: '',
  amount: '',
  description: '',
});

const money = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(amount);
};

const invoiceDate = (value) => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const sortInvoices = (items) => [...items].sort((a, b) => {
  const aTime = new Date(a?.created_at || 0).getTime();
  const bTime = new Date(b?.created_at || 0).getTime();
  return bTime - aTime;
});

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

function InvoiceStat({ label, value, tone = 'default' }) {
  return (
    <article className={`invoices-stat invoices-stat--${tone}`}>
      <span className="invoices-stat-label">{label}</span>
      <strong className="invoices-stat-value">{value}</strong>
    </article>
  );
}

function InvoiceField({ label, children }) {
  return (
    <label className="invoices-field">
      <span className="invoices-field-label">{label}</span>
      {children}
    </label>
  );
}

function InvoiceListItem({ invoice, active, customerName, onSelect }) {
  return (
    <button
      type="button"
      className={`invoices-list-item${active ? ' is-active' : ''}`}
      onClick={() => onSelect(invoice.id)}
    >
      <div className="invoices-list-topline">
        <span className="invoices-list-id">{invoice.id}</span>
        <span className={`jobs-pill jobs-pill--status-${invoice.status || 'pending'}`}>
          {STATUS_LABELS[invoice.status] || invoice.status || 'Pending'}
        </span>
      </div>
      <strong className="invoices-list-amount">{money(invoice.amount)}</strong>
      <div className="invoices-list-meta">
        <span>{customerName}</span>
        <span>{invoiceDate(invoice.created_at)}</span>
      </div>
    </button>
  );
}

export function InvoicesPage() {
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageInvoices = hasFrontendPermission(user, 'invoices.manage');

  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [draft, setDraft] = useState(makeEmptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [invoiceData, jobData, customerData] = await Promise.all([
        apiFetch('/api/invoices', { token }),
        apiFetch('/api/jobs', { token }),
        apiFetch('/api/customers', { token }),
      ]);
      setInvoices(Array.isArray(invoiceData) ? sortInvoices(invoiceData) : []);
      setJobs(Array.isArray(jobData) ? jobData : []);
      setCustomers(Array.isArray(customerData) ? customerData : []);
    } catch (e) {
      setError(e.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const getCustomerName = useCallback((customerId) => {
    const customer = customers.find((item) => item.id === customerId);
    return customer?.name || customerId || 'No customer';
  }, [customers]);

  const getJobTitle = useCallback((jobId) => {
    const job = jobs.find((item) => item.id === jobId);
    return job?.title || jobId || 'No linked job';
  }, [jobs]);

  const filteredInvoices = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return invoices.filter((invoice) => {
      if (statusFilter && invoice.status !== statusFilter) return false;
      if (!term) return true;
      return [
        invoice.id,
        invoice.jobId,
        invoice.customerId,
        getCustomerName(invoice.customerId),
      ].some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [invoices, searchTerm, statusFilter, getCustomerName]);

  useEffect(() => {
    if (!filteredInvoices.length) {
      setSelectedInvoiceId('');
      return;
    }
    if (!filteredInvoices.some((invoice) => invoice.id === selectedInvoiceId)) {
      setSelectedInvoiceId(filteredInvoices[0].id);
    }
  }, [filteredInvoices, selectedInvoiceId]);

  const selectedInvoice = useMemo(
    () => filteredInvoices.find((invoice) => invoice.id === selectedInvoiceId) || null,
    [filteredInvoices, selectedInvoiceId],
  );

  const stats = useMemo(() => ({
    total: invoices.length,
    totalValue: invoices.reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0),
    paidValue: invoices.filter((invoice) => invoice.status === 'paid').reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0),
    pendingValue: invoices.filter((invoice) => invoice.status === 'pending').reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0),
    overdueCount: invoices.filter((invoice) => invoice.status === 'overdue').length,
  }), [invoices]);

  const handleRefresh = () => {
    setRefreshKey((value) => value + 1);
  };

  const handleCreateInvoice = async (event) => {
    event.preventDefault();
    if (!token || !canManageInvoices) return;
    setSaving(true);
    setError('');
    try {
      const created = await apiFetch('/api/invoices', {
        token,
        method: 'POST',
        body: {
          jobId: draft.jobId || '',
          customerId: draft.customerId || '',
          amount: Number(draft.amount || 0),
          description: draft.description || '',
        },
      });
      setInvoices((prev) => sortInvoices([created, ...prev]));
      setSelectedInvoiceId(created.id);
      setDraft(makeEmptyDraft());
      setShowCreateForm(false);
      pushToast(`Created ${created.id}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to create invoice');
      pushToast(e.message || 'Failed to create invoice', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (status) => {
    if (!token || !canManageInvoices || !selectedInvoice) return;
    setSaving(true);
    setError('');
    try {
      const updated = await apiFetch(`/api/invoices/${encodeURIComponent(selectedInvoice.id)}/status`, {
        token,
        method: 'PATCH',
        body: { status },
      });
      setInvoices((prev) => sortInvoices(prev.map((invoice) => (
        invoice.id === updated.id ? updated : invoice
      ))));
      pushToast(`${selectedInvoice.id} marked ${STATUS_LABELS[status].toLowerCase()}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to update invoice');
      pushToast(e.message || 'Failed to update invoice', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedInvoice || !token) return;
    setSaving(true);
    setError('');
    try {
      const response = await fetch(apiUrl(`/api/invoices/${encodeURIComponent(selectedInvoice.id)}/pdf`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `invoice-${selectedInvoice.id}.txt`;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(anchor);
      pushToast(`Downloaded ${selectedInvoice.id}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to download invoice');
      pushToast(e.message || 'Failed to download invoice', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="invoices-page">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="invoices-page">
      <div className="invoices-header">
        <div>
          <span className="invoices-eyebrow">Revenue</span>
          <h1 className="invoices-heading">Invoices</h1>
          <p className="invoices-subheading">
            Billing status, receivables pressure, and downloadable invoice records for completed work.
          </p>
        </div>
        <div className="invoices-header-actions">
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshIcon />
            Refresh
          </Button>
          {canManageInvoices ? (
            <Button onClick={() => setShowCreateForm((value) => !value)}>
              <PlusIcon />
              New Invoice
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <ErrorState message={error} onRetry={loadData} /> : null}

      <div className="invoices-stats-grid">
        <InvoiceStat label="Invoices" value={stats.total} />
        <InvoiceStat label="Total Value" value={money(stats.totalValue)} tone="accent" />
        <InvoiceStat label="Paid" value={money(stats.paidValue)} tone="info" />
        <InvoiceStat label="Pending" value={money(stats.pendingValue)} tone="warning" />
        <InvoiceStat label="Overdue" value={stats.overdueCount} tone="danger" />
      </div>

      {showCreateForm ? (
        <Card className="invoices-create-card" title="Create Invoice">
          <form className="invoices-form-grid" onSubmit={handleCreateInvoice}>
            <InvoiceField label="Job">
              <select
                className="invoices-input"
                value={draft.jobId}
                onChange={(event) => setDraft((prev) => ({ ...prev, jobId: event.target.value }))}
              >
                <option value="">No linked job</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>{job.id} - {job.title}</option>
                ))}
              </select>
            </InvoiceField>
            <InvoiceField label="Customer">
              <select
                className="invoices-input"
                value={draft.customerId}
                onChange={(event) => setDraft((prev) => ({ ...prev, customerId: event.target.value }))}
              >
                <option value="">No linked customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>{customer.name}</option>
                ))}
              </select>
            </InvoiceField>
            <InvoiceField label="Amount">
              <input
                className="invoices-input"
                type="number"
                min="0"
                step="0.01"
                value={draft.amount}
                onChange={(event) => setDraft((prev) => ({ ...prev, amount: event.target.value }))}
                required
              />
            </InvoiceField>
            <InvoiceField label="Description">
              <input
                className="invoices-input"
                value={draft.description}
                onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Labor, emergency dispatch, materials"
              />
            </InvoiceField>
            <div className="invoices-form-actions">
              <Button variant="ghost" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>Create Invoice</Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="invoices-layout">
        <Card className="invoices-list-card" title="Invoice Queue">
          <div className="invoices-toolbar">
            <input
              className="invoices-input"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by invoice, job, or customer"
            />
            <select
              className="invoices-input"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>{STATUS_LABELS[option]}</option>
              ))}
            </select>
          </div>

          {filteredInvoices.length ? (
            <div className="invoices-list">
              {filteredInvoices.map((invoice) => (
                <InvoiceListItem
                  key={invoice.id}
                  invoice={invoice}
                  active={invoice.id === selectedInvoiceId}
                  customerName={getCustomerName(invoice.customerId)}
                  onSelect={setSelectedInvoiceId}
                />
              ))}
            </div>
          ) : (
            <EmptyState title="No invoices in this view" message="Adjust the filters or create a new invoice." />
          )}
        </Card>

        <Card className="invoices-detail-card" title={selectedInvoice ? selectedInvoice.id : 'Details'}>
          {!selectedInvoice ? (
            <EmptyState title="No invoice selected" message="Select an invoice to review billing details and status." />
          ) : (
            <div className="invoices-detail">
              <div className="invoices-detail-head">
                <div className="invoices-detail-topline">
                  <span className="invoices-list-id">{selectedInvoice.id}</span>
                  <span className={`jobs-pill jobs-pill--status-${selectedInvoice.status || 'pending'}`}>
                    {STATUS_LABELS[selectedInvoice.status] || selectedInvoice.status || 'Pending'}
                  </span>
                </div>
                <strong className="invoices-detail-amount">{money(selectedInvoice.amount)}</strong>
                <p className="invoices-detail-copy">
                  {selectedInvoice.description || 'No billing description captured for this invoice yet.'}
                </p>
              </div>

              <div className="invoices-detail-grid">
                <InvoiceField label="Customer">
                  <div className="invoices-readout">{getCustomerName(selectedInvoice.customerId)}</div>
                </InvoiceField>
                <InvoiceField label="Job">
                  <div className="invoices-readout">{getJobTitle(selectedInvoice.jobId)}</div>
                </InvoiceField>
                <InvoiceField label="Created">
                  <div className="invoices-readout">{invoiceDate(selectedInvoice.created_at)}</div>
                </InvoiceField>
                <InvoiceField label="Status">
                  <div className="invoices-readout">{STATUS_LABELS[selectedInvoice.status] || selectedInvoice.status || 'Pending'}</div>
                </InvoiceField>
              </div>

              <div className="invoices-detail-actions">
                <Button variant="secondary" onClick={handleDownload} disabled={saving}>
                  Download
                </Button>
                {canManageInvoices ? (
                  <div className="invoices-status-actions">
                    {STATUS_OPTIONS.map((status) => (
                      <Button
                        key={status}
                        size="sm"
                        variant={selectedInvoice.status === status ? 'primary' : 'secondary'}
                        onClick={() => handleStatusChange(status)}
                        disabled={saving}
                      >
                        {STATUS_LABELS[status]}
                      </Button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
